import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { 
  RefreshCw, 
  Loader2, 
  Search, 
  User, 
  AlertTriangle,
  CheckCircle,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * UserRetryResetPanel - Admin panel to manage user retry counts for job simulations
 * Allows admins to view user progress and reset retry counts when users hit max attempts
 */
const UserRetryResetPanel = ({ api }) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [userProgress, setUserProgress] = useState(null);
  const [resetting, setResetting] = useState(null);

  const searchUserProgress = async () => {
    if (!searchEmail.trim()) {
      toast.error('Please enter a user email');
      return;
    }

    try {
      setSearching(true);
      setUserProgress(null);

      // First get user by email
      const usersRes = await api.get(`/users?email=${encodeURIComponent(searchEmail.trim())}`);
      const users = usersRes.data.users || usersRes.data || [];
      const user = users.find(u => u.email?.toLowerCase() === searchEmail.trim().toLowerCase());

      if (!user) {
        toast.error('User not found');
        return;
      }

      // Get user's skill progress
      const progressRes = await api.get(`/job-simulations/admin/user-progress/${user.id}`);
      setUserProgress(progressRes.data);

    } catch (err) {
      console.error('Failed to search user:', err);
      toast.error(err.response?.data?.detail || 'Failed to search user progress');
    } finally {
      setSearching(false);
    }
  };

  const resetRetryCount = async (skillId, skillName) => {
    if (!userProgress?.user) return;

    try {
      setResetting(skillId);
      
      // Find user ID from the progress data
      const userId = userProgress.progress[0]?.user_id;
      if (!userId) {
        toast.error('User ID not found');
        return;
      }

      await api.post(`/job-simulations/admin/reset-retry/${userId}/${skillId}`);
      
      toast.success(`Retry count reset for "${skillName}"`, {
        description: 'User can now retry the assessment.'
      });

      // Refresh progress data
      const progressRes = await api.get(`/job-simulations/admin/user-progress/${userId}`);
      setUserProgress(progressRes.data);

    } catch (err) {
      console.error('Failed to reset retry:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset retry count');
    } finally {
      setResetting(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RotateCcw className="h-5 w-5" />
          Reset User Retry Count
        </CardTitle>
        <CardDescription>
          Search for a user to view their job simulation progress and reset retry counts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Section */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="user-email" className="sr-only">User Email</Label>
            <Input
              id="user-email"
              placeholder="Enter user email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUserProgress()}
              data-testid="user-email-search"
            />
          </div>
          <Button 
            onClick={searchUserProgress} 
            disabled={searching}
            data-testid="search-user-btn"
          >
            {searching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* User Progress Display */}
        {userProgress && (
          <div className="space-y-4">
            {/* User Info */}
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <User className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="font-medium">{userProgress.user?.name || 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">{userProgress.user?.email}</p>
              </div>
              <Badge variant="outline" className="ml-auto">
                {userProgress.progress_count} skills tracked
              </Badge>
            </div>

            {/* Skills Progress */}
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-3">
                {userProgress.progress?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No skill progress found for this user
                  </p>
                ) : (
                  userProgress.progress.map((skill) => (
                    <div 
                      key={skill.skill_id}
                      className={`p-3 rounded-lg border ${
                        skill.passed
                            ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
                            : 'border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{skill.skill_name || skill.skill_id}</p>
                          <div className="flex items-center gap-2 mt-1 text-sm">
                            {skill.passed ? (
                              <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Passed
                              </Badge>
                            ) : skill.retry_count > 0 ? (
                              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {skill.retry_count} attempt{skill.retry_count === 1 ? '' : 's'}
                              </Badge>
                            ) : (
                              <Badge variant="outline">Not attempted</Badge>
                            )}
                            {skill.overall_score > 0 && (
                              <span className="text-muted-foreground">
                                Score: {skill.overall_score}%
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Reset Button */}
                        {skill.retry_count > 0 && !skill.passed && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                disabled={resetting === skill.skill_id}
                                data-testid={`reset-retry-${skill.skill_id}`}
                              >
                                {resetting === skill.skill_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                    Reset
                                  </>
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Reset Retry Count?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will reset the retry count for &quot;{skill.skill_name || skill.skill_id}&quot; 
                                  and allow the user to attempt the assessment again.
                                  <br /><br />
                                  Current attempts: {skill.retry_count} (retakes are unlimited; this only resets the counter)
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => resetRetryCount(skill.skill_id, skill.skill_name)}
                                  className="bg-amber-600 hover:bg-amber-700"
                                >
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Reset Retry Count
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserRetryResetPanel;
